import axios from "axios";

/**
 * Teleconsultation REST service.
 *
 * - Base URL comes from VITE_API_BASE_URL, falling back to the production API.
 * - The token is read from localStorage at request time (never hardcoded).
 * - If your project already has a shared axios instance, replace `client`
 *   below with an import of that instance and delete the interceptor.
 */

export const API_BASE_URL = (
  import.meta.env?.VITE_API_BASE_URL || "https://cbackend.xraidigital.com/api/"
).replace(/\/?$/, "/");

const TOKEN_KEYS = ["token", "authToken", "access_token", "accessToken"];

export const getAuthToken = () => {
  try {
    for (const key of TOKEN_KEYS) {
      const value = localStorage.getItem(key);
      if (value) {
        return value;
      }
    }
  } catch (error) {
    console.error("Unable to read auth token from localStorage", error);
  }
  return null;
};

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
});

client.interceptors.request.use((config) => {
  const token = getAuthToken();

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Token ${token}`;
  }

  return config;
});

const BASE = "technician/tele-consultations";

const teleConsultationApi = {
  createConsultation: async (payload) => {
    const response = await client.post(`${BASE}/create/`, payload);
    return response.data;
  },

  getConsultation: async (consultationId) => {
    const response = await client.get(`${BASE}/${consultationId}/`);
    return response.data;
  },

  // Doctor-only action. The technician page must never call this.
  startConsultation: async (consultationId) => {
    const response = await client.post(`${BASE}/${consultationId}/start/`);
    return response.data;
  },

  joinConsultation: async (
    consultationId,
    payload = { participant_type: "TECHNICIAN" }
  ) => {
    const response = await client.post(
      `${BASE}/${consultationId}/join/`,
      payload
    );
    return response.data;
  },

  leaveConsultation: async (
    consultationId,
    payload = { participant_type: "TECHNICIAN" }
  ) => {
    const response = await client.post(
      `${BASE}/${consultationId}/leave/`,
      payload
    );
    return response.data;
  },

  // Doctor-only action. The technician page must never call this.
  endConsultation: async (consultationId) => {
    const response = await client.post(`${BASE}/${consultationId}/end/`);
    return response.data;
  },

  // formData must contain `file` (and optionally `duration_seconds`).
  uploadRecording: async (consultationId, formData) => {
    const response = await client.post(
      `${BASE}/${consultationId}/recording/`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      }
    );
    return response.data;
  },
};

export default teleConsultationApi;